/**
 * Create/Copy Agent Dialog
 * Dialog for creating new agents or copying existing ones
 */

import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { agentApi } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useI18n } from '@/i18n';
import { X, Copy, Plus } from 'lucide-react';
import type { AgentConfiguration } from '@/types';

interface CreateAgentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (agentType: string) => void;
  copyFrom?: {
    agentType: string;
    config: Partial<AgentConfiguration>;
  };
}

export const CreateAgentDialog: React.FC<CreateAgentDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
  copyFrom,
}) => {
  const queryClient = useQueryClient();
  const { messages } = useI18n();
  const t = messages.agentConfig.createAgentDialog;
  const tc = messages.common;
  const [agentType, setAgentType] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (copyFrom) {
      setAgentType(copyFrom.agentType + '_copy');
      setName((copyFrom.config.name || '') + ' (Copy)');
      setDescription(copyFrom.config.description || '');
    } else {
      setAgentType('');
      setName('');
      setDescription('');
    }
  }, [copyFrom, isOpen]);

  const createMutation = useMutation({
    mutationFn: (config: AgentConfiguration) => agentApi.create(config),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['agents-list'] });
      onSuccess(variables.agent_type || '');
      onClose();
    },
    onError: (error: Error) => {
      setError(error.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!agentType.trim()) {
      setError(t.agentTypeRequired);
      return;
    }

    if (!name.trim()) {
      setError(t.nameRequired);
      return;
    }

    // Validate agent type format (allow lowercase letters, numbers, underscores, and hyphens)
    if (!/^[a-z][a-z0-9_-]*$/.test(agentType)) {
      setError(t.agentTypeInvalid);
      return;
    }

    const newConfig: AgentConfiguration = {
      ...(copyFrom?.config || {}),
      agent_type: agentType,
      name,
      description,
    } as AgentConfiguration;

    createMutation.mutate(newConfig);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            {copyFrom ? (
              <Copy className="w-5 h-5 text-slate-500" />
            ) : (
              <Plus className="w-5 h-5 text-slate-500" />
            )}
            <h2 className="text-lg font-semibold text-slate-900">
              {copyFrom ? t.copyAgent : t.createNewAgent}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t.agentType} <span className="text-red-500">*</span>
            </label>
            <Input
              value={agentType}
              onChange={(e) => setAgentType(e.target.value.toLowerCase())}
              placeholder={t.agentTypePlaceholder}
              className="font-mono"
            />
            <p className="mt-1 text-xs text-slate-500">
              {t.agentTypeHint}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t.name} <span className="text-red-500">*</span>
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t.namePlaceholder}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t.description}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t.descriptionPlaceholder}
              className="w-full p-2 border border-slate-300 rounded-lg resize-none h-20"
            />
          </div>

          {copyFrom && (
            <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
              <p>
                {t.copyingFrom}: <strong>{copyFrom.agentType}</strong>
              </p>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" type="button" onClick={onClose}>
              {tc.cancel}
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? t.creating : copyFrom ? t.copyAgent : t.createNewAgent}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateAgentDialog;
